import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import DashboardLayout from '../../components/DashboardLayout';
import { apiRequest } from '../../utils/api';
import { getModulePermissions } from '../../utils/permissions';
import { getActiveCompanyId } from '../../utils/companyContext';

const OutletSettings = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    branchName: '',
    address: '',
    mobile: '',
    areaName: '',
    showId: '',
    slug: '',
    companyId: '',
  });
  const [outlets, setOutlets] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [showIdError, setShowIdError] = useState('');
  const [generatingShowId, setGeneratingShowId] = useState(false);
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [filterQuery, setFilterQuery] = useState('');
  const [activeCompanyId, setActiveCompanyId] = useState(getActiveCompanyId());
  const activeCompanyValue = activeCompanyId ? String(activeCompanyId) : '';
  const activeCompany = companies.find((company) => String(company.id) === activeCompanyValue);
  const isEditing = Boolean(editingId);

  const permissions = useMemo(() => getModulePermissions('settings-outlet'), []);
  const canCreate = permissions.create;
  const canEdit = permissions.edit;
  const canDelete = permissions.delete;
  const canManage = canCreate || canEdit;
  const hasActions = canManage || canDelete;
  const isViewOnly = permissions.view && !hasActions;

  const resetForm = () => {
    setFormData({ branchName: '', address: '', mobile: '', areaName: '', showId: '', slug: '', companyId: activeCompanyValue });
    setEditingId(null);
    setFormErrors({});
    setShowIdError('');
  };

  const fetchNextShowId = useCallback(async () => {
    setShowIdError('');
    setGeneratingShowId(true);
    try {
      const response = await apiRequest(
        '/api/admin/settings/outlets/next-show-id',
        {},
        navigate,
      );
      const nextShowId = response.data?.nextShowId || '';
      setFormData((prev) => ({
        ...prev,
        showId: nextShowId,
      }));
    } catch (err) {
      setShowIdError(err.message || 'Unable to generate show ID.');
    } finally {
      setGeneratingShowId(false);
    }
  }, [navigate]);

  const fetchCompanies = useCallback(async () => {
    try {
      const response = await apiRequest('/api/admin/companies', {}, navigate);
      setCompanies(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err.message || 'Unable to load companies.');
    }
  }, [navigate]);

  const fetchOutlets = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (!activeCompanyValue) {
        setOutlets([]);
        setError('Please select a company from the top header.');
        return;
      }

      const query = new URLSearchParams({ companyId: activeCompanyValue });
      const endpoint = `/api/admin/settings/outlets?${query.toString()}`;
      const response = await apiRequest(endpoint, {}, navigate);
      setOutlets(response.data || []);
    } catch (err) {
      setError(err.message || 'Unable to load outlets.');
    } finally {
      setLoading(false);
    }
  }, [activeCompanyValue, navigate]);

  useEffect(() => {
    fetchCompanies();
    fetchNextShowId();
  }, [fetchCompanies, fetchNextShowId]);

  useEffect(() => {
    fetchOutlets();
  }, [fetchOutlets]);

  useEffect(() => {
    const syncActiveCompany = () => setActiveCompanyId(getActiveCompanyId());
    window.addEventListener('activeCompanyChanged', syncActiveCompany);
    window.addEventListener('storage', syncActiveCompany);
    return () => {
      window.removeEventListener('activeCompanyChanged', syncActiveCompany);
      window.removeEventListener('storage', syncActiveCompany);
    };
  }, []);

  useEffect(() => {
    setFormData((prev) => ({ ...prev, companyId: activeCompanyValue }));
  }, [activeCompanyValue]);

  useEffect(() => {
    if (isEditing) {
      setIsFormVisible(true);
    }
  }, [isEditing]);

  const normalizedFilterQuery = filterQuery.trim().toLowerCase();

  const filteredOutlets = useMemo(() => {
    if (!normalizedFilterQuery) {
      return outlets;
    }

    return outlets.filter((outlet) => {
      const branchName = (outlet.branchName || '').toLowerCase();
      const mobile = (outlet.mobile || '').toLowerCase();
      const areaName = (outlet.areaName || '').toLowerCase();
      const companyName = (outlet.companyName || '').toLowerCase();

      return (
        branchName.includes(normalizedFilterQuery) ||
        mobile.includes(normalizedFilterQuery) ||
        areaName.includes(normalizedFilterQuery) ||
        companyName.includes(normalizedFilterQuery)
      );
    });
  }, [outlets, normalizedFilterQuery]);

  const showForm = (isFormVisible || isEditing) && canManage;

  const handleStartCreate = () => {
    if (!canCreate) {
      setError('You do not have permission to create outlets.');
      return;
    }
    resetForm();
    setError('');
    setSuccessMessage('');
    setIsFormVisible(true);
    fetchNextShowId();
  };

  const handleCancel = () => {
    resetForm();
    setIsFormVisible(false);
    setError('');
    setSuccessMessage('');
    fetchNextShowId();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isEditing && !canEdit) {
      setError('You do not have permission to edit outlets.');
      return;
    }

    if (!isEditing && !canCreate) {
      setError('You do not have permission to create outlets.');
      return;
    }

    setError('');
    setSuccessMessage('');
    const trimmedBranch = formData.branchName.trim();
    const trimmedShowId = formData.showId.trim();
    const errors = {};

    if (!trimmedBranch) {
      errors.branchName = 'Branch name is required.';
    }

    if (!trimmedShowId) {
      errors.showId = 'Store show ID is required.';
    }

    if (!formData.companyId) {
      errors.companyId = 'Company is required.';
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setSubmitting(true);
    setFormErrors({});
    setShowIdError('');

    const payload = {
      branchName: trimmedBranch,
      address: formData.address.trim() || null,
      mobile: formData.mobile.trim() || null,
      areaName: formData.areaName.trim() || null,
      showId: trimmedShowId || null,
      companyId: Number(formData.companyId),
    };

    try {
      const endpoint = isEditing
        ? `/api/admin/settings/outlets/${editingId}`
        : '/api/admin/settings/outlets';
      const method = isEditing ? 'PUT' : 'POST';

      const response = await apiRequest(
        endpoint,
        { method, body: payload },
        navigate
      );

      setSuccessMessage(
        response.message || (isEditing ? 'Outlet updated successfully.' : 'Outlet added successfully.')
      );
      resetForm();
      fetchOutlets();
      fetchNextShowId();
      setIsFormVisible(false);
    } catch (err) {
      if (err.status === 409) {
        setFormErrors((prev) => ({ ...prev, showId: err.message || 'Outlet show ID already exists.' }));
      } else {
        setError(err.message || 'Unable to save outlet.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (outlet) => {
    if (!canEdit) {
      navigate('/access-denied', { state: { from: '/settings/outlet' } });
      return;
    }
    setEditingId(outlet.id);
    setIsFormVisible(true);
    setFormData({
      branchName: outlet.branchName || '',
      address: outlet.address || '',
      mobile: outlet.mobile || '',
      areaName: outlet.areaName || '',
      showId: outlet.showId || '',
      slug: outlet.slug || '',
      companyId: activeCompanyValue,
    });
    setSuccessMessage('');
    setError('');
    setFormErrors({});
    setShowIdError('');
  };

  const handleDelete = async (id) => {
    if (!canDelete) {
      navigate('/access-denied', { state: { from: '/settings/outlet' } });
      return;
    }

    const result = await Swal.fire({
      title: 'Delete outlet?',
      text: 'Are you sure you want to delete this outlet? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#6c757d',
      confirmButtonText: 'Yes, delete',
    });

    if (!result.isConfirmed) {
      return;
    }

    setActionId(id);
    setError('');
    setSuccessMessage('');
    try {
      const response = await apiRequest(
        `/api/admin/settings/outlets/${id}`,
        { method: 'DELETE' },
        navigate
      );
      setSuccessMessage(response.message || 'Outlet deleted successfully.');
      if (editingId === id) {
        resetForm();
        fetchNextShowId();
        setIsFormVisible(false);
      }
      fetchOutlets();
    } catch (err) {
      setError(err.message || 'Unable to delete outlet.');
    } finally {
      setActionId(null);
    }
  };

  return (
    <DashboardLayout>
      <div className="container py-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <h4 className="mb-0">Outlet Settings</h4>
          <div className="d-flex gap-2">
            {!showForm && canCreate && (
              <button type="button" className="btn btn-primary" onClick={handleStartCreate}>
                Create Outlet
              </button>
            )}
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => navigate('/settings')}
            >
              Back
            </button>
          </div>
        </div>

        {isViewOnly && (
          <div className="alert alert-info" role="alert">
            You have view-only access. Create and edit actions are disabled.
          </div>
        )}

        {error && (
          <div className="alert alert-danger" role="alert">
            {error}
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success" role="alert">
            {successMessage}
          </div>
        )}

        {showForm && (
          <div className="row">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title mb-4">{isEditing ? 'Update Outlet' : 'Add Outlet'}</h5>

                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="text"
                          className={`form-control ${formErrors.showId ? 'is-invalid' : ''}`}
                          id="showId"
                          placeholder="Enter Store Show ID"
                          value={formData.showId}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\s+/g, '').toUpperCase();
                            setFormData((prev) => ({ ...prev, showId: value }));
                            setFormErrors((prevErrors) => {
                              if (!prevErrors.showId) {
                                return prevErrors;
                              }
                              const nextErrors = { ...prevErrors };
                              delete nextErrors.showId;
                              return nextErrors;
                            });
                          }}
                        />
                        <label htmlFor="showId">Store Show ID *</label>
                        {formErrors.showId && (
                          <div className="invalid-feedback d-block">{formErrors.showId}</div>
                        )}
                        {showIdError && !formErrors.showId && (
                          <div className="text-danger small mt-1">{showIdError}</div>
                        )}
                      </div>
                      <div className="d-flex justify-content-end mt-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={fetchNextShowId}
                          disabled={generatingShowId || submitting || isEditing || !activeCompanyValue}
                        >
                          {generatingShowId ? 'Generating…' : 'Generate ID'}
                        </button>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-floating">
                        <select
                          className={`form-select ${formErrors.companyId ? 'is-invalid' : ''}`}
                          id="companyId"
                          value={formData.companyId}
                          onChange={() => {}}
                          disabled
                        >
                          {activeCompanyValue && activeCompany ? (
                            <option value={activeCompanyValue}>{activeCompany.name}</option>
                          ) : (
                            <option value="">Select Company</option>
                          )}
                        </select>
                        <label htmlFor="companyId">Company *</label>
                        {formErrors.companyId && (
                          <div className="invalid-feedback d-block">{formErrors.companyId}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="text"
                          className={`form-control ${formErrors.branchName ? 'is-invalid' : ''}`}
                          id="branchName"
                          placeholder="Enter Branch Name"
                          value={formData.branchName}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, branchName: value }));
                            setFormErrors((prevErrors) => {
                              if (!prevErrors.branchName) {
                                return prevErrors;
                              }
                              const nextErrors = { ...prevErrors };
                              delete nextErrors.branchName;
                              return nextErrors;
                            });
                          }}
                        />
                        <label htmlFor="branchName">Branch Name *</label>
                        {formErrors.branchName && (
                          <div className="invalid-feedback d-block">{formErrors.branchName}</div>
                        )}
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="address"
                          placeholder="Enter Address"
                          value={formData.address}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, address: e.target.value }))
                          }
                        />
                        <label htmlFor="address">Address</label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="tel"
                          className="form-control"
                          id="mobile"
                          placeholder="Enter Mobile Number"
                          value={formData.mobile}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, mobile: e.target.value }))
                          }
                        />
                        <label htmlFor="mobile">Mobile Number</label>
                      </div>
                    </div>

                    <div className="col-md-4">
                      <div className="form-floating">
                        <input
                          type="text"
                          className="form-control"
                          id="areaName"
                          placeholder="Enter Area Name"
                          value={formData.areaName}
                          onChange={(e) =>
                            setFormData((prev) => ({ ...prev, areaName: e.target.value }))
                          }
                        />
                        <label htmlFor="areaName">Area Name</label>
                      </div>
                    </div>

                    {isEditing && (
                      <div className="col-md-4">
                        <div className="form-floating">
                          <input
                            type="text"
                            className="form-control"
                            id="slug"
                            value={formData.slug}
                            placeholder="Slug"
                            disabled
                            readOnly
                          />
                          <label htmlFor="slug">Slug</label>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="mt-4">
                    <button type="submit" className="btn btn-danger" disabled={submitting}>
                      {submitting ? (isEditing ? 'Updating...' : 'Saving...') : isEditing ? 'Update' : 'Save'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary ms-2"
                      onClick={handleCancel}
                      disabled={submitting}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        )}

        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">Existing Outlets</h5>
                <div className="row g-3 align-items-end mb-3">
                  <div className="col-md-6 col-lg-4">
                    <div className="form-floating">
                      <select
                        className="form-select"
                        id="outlet-company-filter"
                        value={activeCompanyValue}
                        onChange={() => {}}
                        disabled
                      >
                        {activeCompanyValue && activeCompany ? (
                          <option value={activeCompanyValue}>{activeCompany.name}</option>
                        ) : (
                          <option value="">Select Company</option>
                        )}
                      </select>
                      <label htmlFor="outlet-company-filter">Company</label>
                    </div>
                  </div>
                  <div className="col-md-6 col-lg-4">
                    <div className="form-floating">
                      <input
                        type="search"
                        className="form-control"
                        id="outlet-filter"
                        placeholder="Search Outlets"
                        value={filterQuery}
                        onChange={(event) => setFilterQuery(event.target.value)}
                      />
                      <label htmlFor="outlet-filter">Search Outlets</label>
                    </div>
                  </div>
                  {filterQuery && (
                    <div className="col-auto">
                      <button
                        type="button"
                        className="btn btn-outline-secondary"
                        onClick={() => setFilterQuery('')}
                      >
                        Clear
                      </button>
                    </div>
                  )}
                </div>

                {loading ? (
                  <p className="text-muted mb-0">Loading outlets...</p>
                ) : outlets.length === 0 ? (
                  <p className="text-muted mb-0">No outlets found.</p>
                ) : filteredOutlets.length === 0 ? (
                  <p className="text-muted mb-0">No outlets match your search.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>S.No</th>
                          <th>Show ID</th>
                          <th>Company</th>
                          <th>Branch Name</th>
                          <th>Address</th>
                          <th>Mobile</th>
                          <th>Area Name</th>
                          <th>Slug</th>
                          {hasActions && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOutlets.map((outlet, index) => (
                          <tr key={outlet.id}>
                            <td>{index + 1}</td>
                            <td>{outlet.showId}</td>
                            <td>{outlet.companyName || '-'}</td>
                            <td>{outlet.branchName}</td>
                            <td>{outlet.address || '-'}</td>
                            <td>{outlet.mobile || '-'}</td>
                            <td>{outlet.areaName || '-'}</td>
                            <td>{outlet.slug}</td>
                            {hasActions && (
                              <td>
                                {canEdit && (
                                  <button
                                    className="btn btn-sm btn-warning me-2"
                                    onClick={() => handleEdit(outlet)}
                                    disabled={submitting || actionId !== null}
                                  >
                                    Edit
                                  </button>
                                )}
                                {canDelete && (
                                  <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(outlet.id)}
                                    disabled={actionId === outlet.id || submitting}
                                  >
                                    {actionId === outlet.id ? 'Deleting...' : 'Delete'}
                                  </button>
                                )}
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default OutletSettings;